import { createApiClient, getMe, logout } from '@kiao-voice/api-client'
import type { MeResponse, TokenStorage } from '@kiao-voice/api-client'
import { getWebApiOrigin } from '../../lib/api'
import { reportHandledException } from '../../lib/error-reporting'
import { clearHintSuggestionData } from '../dictionary/hint-suggestions'
import { clearRememberedTemplates } from './remembered-template'
import {
  TOKEN_STORAGE_KEY,
  clearStoredToken,
  readStoredToken,
  storeToken,
} from '../auth/token-storage'

/**
 * Token storage primitives, re-exported unchanged from `../auth/token-storage`
 * (the canonical implementation — used directly by the OTP login flow, the
 * shared web API client, and every `onAuthTokenChange` listener: navbar,
 * `AuthGuard`, `admin-gate`).
 *
 * This module used to carry its OWN duplicate implementation (same
 * `TOKEN_STORAGE_KEY`, same legacy-key migration, but a DIFFERENT
 * `clearStoredToken()` ordering — token removed before `clearActiveContext()`
 * — and no `AUTH_TOKEN_CHANGE_EVENT` dispatch at all). Two copies of the same
 * "clear a session" logic drifting independently is exactly how the round-3
 * sign-out bug happened: `LogoutFlash` (the actual sign-out entry point)
 * imports `clearStoredToken` from HERE, not from `../auth/token-storage`, so
 * a fix applied to only the canonical copy (round-2's re-probe-on-clear
 * change) silently didn't apply to the path real users hit. Re-exporting
 * removes the possibility of the two ever disagreeing again.
 *
 * The internal batch-transcription console and the SaaS dashboard share the
 * same `oat_*` bearer token (it's the same user, the same authentication
 * artifact) — see `../auth/token-storage` for the storage-key history and the
 * legacy-key migration rationale.
 */
export { TOKEN_STORAGE_KEY, clearStoredToken, readStoredToken, storeToken }

const tokenStorage: TokenStorage = {
  async getAccessToken() {
    return readStoredToken()
  },
  async setAccessToken(token: string) {
    storeToken(token)
  },
  async clearTokens() {
    clearStoredToken()
  },
}

function makeClient() {
  return createApiClient({ baseUrl: getWebApiOrigin(), tokenStorage })
}

/**
 * The single sign-out entry point. Clears the local session immediately —
 * synchronously, before the first `await` — so the caller is really signed
 * out regardless of whether the server-side revoke succeeds, hangs, or the
 * network is offline. Every 登出 entry point (`LogoutFlash`, and
 * `ConnectedAppShell`'s default) calls this and ONLY this — there is no
 * second `clearStoredToken()` anywhere else in the sign-out path.
 *
 * The token MUST be captured before clearing: `logout()`'s underlying
 * api-client reads the bearer token live from `tokenStorage.getAccessToken()`
 * at request time, which reads `readStoredToken()`. Clearing first (the old
 * bug — `LogoutFlash` used to call `clearStoredToken()` itself BEFORE calling
 * this function) means the DELETE `/auth/logout` request goes out with no
 * `Authorization` header, gets a 401, and the server-side `oat_*` token is
 * never actually revoked — it stays valid for up to 30 days despite the
 * comment claiming the request "still fires as a courtesy". Capturing the
 * token here and revoking with a client pinned to that captured value fixes
 * that: the revoke always carries the token the user was actually signed in
 * with, independent of local storage having already been cleared.
 */
export async function signOut(): Promise<void> {
  const token = readStoredToken()
  clearStoredToken()
  // Explicit sign-out is where the frecency hint store gets cleared: it holds
  // the user's hand-typed proper nouns and must not surface as suggestions for
  // the next account on a shared machine. NOT done in `clearStoredToken` —
  // that is also the 401 token-expiry eviction path (30-day TTL), where wiping
  // would destroy the same user's own data on a routine expiry.
  clearHintSuggestionData()
  // Same contract for the template the upload dialog opens on: its key carries
  // the workspace but no account, so the next account must not inherit it.
  clearRememberedTemplates()
  if (!token) return
  await revokeToken(token)
}

/** `DELETE /auth/logout` with a client pinned to `token`, whatever storage holds by then. */
async function revokeToken(token: string): Promise<void> {
  const revokeClient = createApiClient({
    baseUrl: getWebApiOrigin(),
    tokenStorage: {
      async getAccessToken() {
        return token
      },
      async setAccessToken() {
        // Revoke-only client — nothing writes a new token through this path.
      },
      async clearTokens() {
        // Already cleared by the caller; nothing left for this client to clear.
      },
    },
  })
  await logout(revokeClient)
}

/**
 * A failed revoke is reported, not surfaced: the session is already gone from
 * this device, which is what the caller's UI depends on, and the token that
 * survives on the server expires by TTL.
 */
function reportFailedRevoke(source: string) {
  return (err: unknown) => {
    reportHandledException(err, { source })
    console.warn(
      `[${source}] server-side session revoke failed; local session already cleared:`,
      err
    )
  }
}

/**
 * `signOut()` for the "use a different account" entry points, which move on to
 * the next sign-in right away. The local session is gone when this returns
 * (`signOut()` clears it before its first `await`); the server-side revoke of
 * the token held until now finishes in the background and never blocks the
 * switch.
 */
export function signOutInBackground(source: string): void {
  void signOut().catch(reportFailedRevoke(source))
}

/**
 * Stores a session minted by a sign-in that can happen while another one is
 * already stored (a magic link opened in a signed-in browser). The token it
 * replaces is revoked server-side in the background rather than left alive
 * for its 30-day TTL.
 */
export function replaceStoredToken(token: string, source: string): void {
  const previous = readStoredToken()
  storeToken(token)
  if (previous && previous !== token) void revokeToken(previous).catch(reportFailedRevoke(source))
}

export async function fetchCurrentUser(): Promise<MeResponse> {
  return getMe(makeClient())
}

/**
 * Sentinel error used by `loadInternalSession` to signal that the bootstrap
 * call hit an auth/forbidden response — callers should drop the local
 * token and bounce to `/login`. Anything else (5xx, network down) is
 * surfaced as the original error so transient outages don't sign people
 * out unnecessarily.
 */
export class InternalSessionAuthError extends Error {
  readonly status: 401 | 403
  constructor(status: 401 | 403, message: string) {
    super(message)
    this.name = 'InternalSessionAuthError'
    this.status = status
  }
}
